import Bugsnag from '@bugsnag/browser';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ParameterInfo } from '@src/core/hooks';
import { cartAuth, getClient, type AppDispatch, type RootState } from '@src/core/store';
import type { AddCartItemRequest, UpsellOffer } from '@src/core/buyerApiClient';
import { loadAffiliationCode } from '@src/common';
import {
  adderResumeSelector,
  adderState,
  createAddToCartItemRequestPayload,
  productAdderStepState,
} from '@src/stores/product';
import {
  abandonMultiProductBooking,
  multiProductBookingSelector,
  multiProductBookingTotalCentsSelector,
  setAddingToCart,
  startMultiProductBooking as coreStartMultiProductBooking,
  type MultiProductBookingState,
} from '@src/stores/multiProductBooking';
import {
  trackUpsellBookingAbandoned,
  trackUpsellCompleted,
  trackUpsellProductSelected,
  trackUpsellSkipped,
} from '@src/domains/products/features/productUpsell/analytics/upsellTracking';

export type { MultiProductBookingState as UpsellBookingState };

interface ProductUpsellOffersState {
  offersByKey: Record<string, UpsellOffer[]>;
  loadingByKey: Record<string, boolean>;
}

export interface ProductUpsellModalState {
  sourceProductId: string;
  widgetId: string;
  params: ParameterInfo;
}

export interface ProductUpsellState {
  offersState: ProductUpsellOffersState;
  modal?: ProductUpsellModalState;
  selectedOfferProductIds: Record<string, true>;
  hasEverSelectedUpsellProduct: boolean;
}

const initialState: ProductUpsellState = {
  offersState: {
    offersByKey: {},
    loadingByKey: {},
  },
  selectedOfferProductIds: {},
  hasEverSelectedUpsellProduct: false,
};

const getUpsellOffersKey = (productId: string, cartId: string) => `${productId}:${cartId}`;

const productUpsellSlice = createSlice({
  name: 'productUpsell',
  initialState,
  reducers: {
    setUpsellOffersLoading: {
      reducer(
        state,
        { payload, meta: { productId, cartId } }: PayloadAction<boolean, string, { productId: string; cartId: string }>,
      ) {
        const key = getUpsellOffersKey(productId, cartId);
        state.offersState.loadingByKey[key] = payload;
      },
      prepare(productId: string, cartId: string, loading: boolean) {
        return { payload: loading, meta: { productId, cartId } };
      },
    },
    setUpsellOffers: {
      reducer(
        state,
        {
          payload,
          meta: { productId, cartId },
        }: PayloadAction<UpsellOffer[], string, { productId: string; cartId: string }>,
      ) {
        const key = getUpsellOffersKey(productId, cartId);
        state.offersState.offersByKey[key] = payload;
      },
      prepare(productId: string, cartId: string, offers: UpsellOffer[]) {
        return { payload: offers, meta: { productId, cartId } };
      },
    },
    clearUpsellOffers: (state: ProductUpsellState, { payload: productId }: PayloadAction<string>) => {
      const productKeyPrefix = `${productId}:`;

      Object.keys(state.offersState.offersByKey).forEach((key) => {
        if (key.startsWith(productKeyPrefix)) {
          delete state.offersState.offersByKey[key];
        }
      });
      Object.keys(state.offersState.loadingByKey).forEach((key) => {
        if (key.startsWith(productKeyPrefix)) {
          delete state.offersState.loadingByKey[key];
        }
      });
    },
    openProductUpsellModal: {
      reducer(state, { payload }: PayloadAction<{ sourceProductId: string; widgetId: string; params: ParameterInfo }>) {
        state.modal = payload;
        state.selectedOfferProductIds = {};
        state.hasEverSelectedUpsellProduct = false;
      },
      prepare(sourceProductId: string, widgetId: string, params: ParameterInfo) {
        return { payload: { sourceProductId, widgetId, params } };
      },
    },
    closeProductUpsellModal: (state: ProductUpsellState) => {
      state.modal = undefined;
    },
    setProductUpsellSelectedProductIds: (state: ProductUpsellState, { payload }: PayloadAction<string[]>) => {
      state.selectedOfferProductIds = Object.fromEntries(payload.map((productId) => [productId, true]));
    },
    toggleProductUpsellSelectedProductId: (
      state: ProductUpsellState,
      { payload: productId }: PayloadAction<string>,
    ) => {
      if (state.selectedOfferProductIds[productId]) {
        delete state.selectedOfferProductIds[productId];
        return;
      }

      state.selectedOfferProductIds[productId] = true;
      state.hasEverSelectedUpsellProduct = true;
    },
  },
});

export default productUpsellSlice.reducer;

export const {
  setUpsellOffersLoading,
  setUpsellOffers,
  clearUpsellOffers,
  openProductUpsellModal,
  closeProductUpsellModal,
  setProductUpsellSelectedProductIds,
  toggleProductUpsellSelectedProductId,
} = productUpsellSlice.actions;

export const loadProductUpsellOffers =
  (productId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const cartId = state.cart.cart?.id;
    if (!cartId) {
      return;
    }

    const upsellOffersKey = getUpsellOffersKey(productId, cartId);

    if (state.productUpsell.offersState.loadingByKey[upsellOffersKey]) {
      return;
    }

    if (state.productUpsell.offersState.offersByKey[upsellOffersKey] !== undefined) {
      return;
    }

    dispatch(setUpsellOffersLoading(productId, cartId, true));

    try {
      const { data: offers } = await getClient(state).upsellOffers.getProductUpsellOffers(productId, cartId);
      dispatch(setUpsellOffers(productId, cartId, offers));
    } catch (error) {
      dispatch(setUpsellOffers(productId, cartId, []));
    } finally {
      dispatch(setUpsellOffersLoading(productId, cartId, false));
    }
  };

export const toggleUpsellProductSelection =
  (sourceProductId: string, productId: string, upsellProductSellerId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const isSelecting = !getState().productUpsell.selectedOfferProductIds[productId];

    if (isSelecting) {
      trackUpsellProductSelected(sourceProductId, productId, upsellProductSellerId);
    }

    dispatch(toggleProductUpsellSelectedProductId(productId));
  };

export const closeUpsellModal = (timeOnModalMs: number) => (dispatch: AppDispatch, getState: () => RootState) => {
  const { modal, selectedOfferProductIds, hasEverSelectedUpsellProduct } = getState().productUpsell;

  if (modal && Object.keys(selectedOfferProductIds).length === 0 && !hasEverSelectedUpsellProduct) {
    trackUpsellSkipped(modal.sourceProductId, timeOnModalMs);
  }

  dispatch(closeProductUpsellModal());
};

export const startMultiProductBooking =
  (
    widgetId: string,
    sourceProductId: string,
    productIds: string[],
    params: ParameterInfo,
    autoSelectFirstProduct = false,
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const cartId = state.cart.cart?.id ?? '';
    const offers = state.productUpsell.offersState.offersByKey[getUpsellOffersKey(sourceProductId, cartId)] ?? [];
    const validProductIds = productIds.filter((id) => offers.some((o) => o.productId === id));

    if (validProductIds.length === 0) return;

    dispatch(coreStartMultiProductBooking(widgetId, sourceProductId, validProductIds, params, autoSelectFirstProduct));
    dispatch(closeProductUpsellModal());
  };

export const addUpsellItemsToCart = (widgetId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const booking = multiProductBookingSelector(state);
  const cart = state.cart.cart;

  if (!booking || !cart) return;

  dispatch(setAddingToCart(true));

  const { productSelectionState } = adderState(widgetId)(state);
  const offers =
    state.productUpsell.offersState.offersByKey[getUpsellOffersKey(booking.sourceProductId, cart.id)] ?? [];
  const [affiliationCode] = loadAffiliationCode();

  const items = booking.productIds.map((productId) => {
    const adderRes = adderResumeSelector(widgetId, productId)(state);
    const selectionState = productSelectionState[productId];
    const offer = offers.find((o) => o.productId === productId);

    const item = createAddToCartItemRequestPayload({
      productId,
      selectedAdditionals: adderRes.selectedAdditionals,
      selectedResources: adderRes.selectedResources,
      selectedTariffs: adderRes.selectedTariffs,
      selectedDate: adderRes.selectedDate,
      selectedTariffHour: adderRes.selectedTariffHour,
      tariffVerificationValues: selectionState?.tariffVerificationValues ?? [],
      selectedAttributes: selectionState?.selectedAttributes ?? [],
    }) as AddCartItemRequest;

    return {
      ...item,
      upsellOfferId: offer?.id ?? null,
      ...(affiliationCode ? { affiliationCode } : {}),
    };
  });

  try {
    await getClient(state).cartItems.addCartItems(cart.id, { items }, cartAuth(state));
  } catch (e) {
    Bugsnag.notify({ name: 'UpsellAddToCartError', message: 'addCartItems failed during upsell booking' }, (event) => {
      event.addMetadata('error', { message: (e as Error)?.message, status: (e as { status?: number })?.status });
    });
    dispatch(setAddingToCart(false));
    return;
  }

  trackUpsellCompleted(
    booking.sourceProductId,
    booking.productIds.length,
    multiProductBookingTotalCentsSelector(widgetId)(state),
  );

  dispatch(abandonMultiProductBooking());
};

export const abandonUpsellBooking = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const booking = multiProductBookingSelector(state);

  if (booking) {
    const currentProductId = adderState(booking.widgetId)(state).productId;
    const { currentStep } = productAdderStepState(booking.widgetId, currentProductId)(state);
    trackUpsellBookingAbandoned(
      booking.sourceProductId,
      currentProductId || undefined,
      currentProductId ? currentStep : undefined,
    );
  }

  dispatch(abandonMultiProductBooking());
};

const EMPTY_UPSELL_OFFERS: UpsellOffer[] = [];

export const productUpsellOffersStatusSelector = (productId: string) =>
  createSelector(
    (state: RootState) => {
      const cartId = state.cart.cart?.id;

      if (!cartId) {
        return undefined;
      }

      const key = getUpsellOffersKey(productId, cartId);
      return state.productUpsell.offersState.offersByKey[key];
    },
    (state: RootState) => {
      const cartId = state.cart.cart?.id;

      if (!cartId) {
        return undefined;
      }

      const key = getUpsellOffersKey(productId, cartId);
      return state.productUpsell.offersState.loadingByKey[key];
    },
    (offers, isLoading) => ({
      offers: offers ?? EMPTY_UPSELL_OFFERS,
      hasLoaded: offers !== undefined,
      isLoading: Boolean(isLoading),
    }),
  );

export const productUpsellModalSelector = (state: RootState) => state.productUpsell.modal;

export const productUpsellSelectedProductIdsSelector = createSelector(
  (state: RootState) => state.productUpsell.selectedOfferProductIds,
  (selectedProductIds) => Object.keys(selectedProductIds),
);

export const upsellBookingProductsSelector = createSelector(
  (state: RootState) => state.multiProductBooking.booking,
  (state: RootState) => state.productUpsell.offersState.offersByKey,
  (state: RootState) => state.cart.cart?.id,
  (booking, offersByKey, cartId) => {
    if (!booking || !cartId) return [];
    const offers = offersByKey[getUpsellOffersKey(booking.sourceProductId, cartId)] ?? [];
    return booking.productIds
      .map((id) => offers.find((o) => o.productId === id)?.product)
      .filter((p): p is NonNullable<typeof p> => Boolean(p));
  },
);
